import type { FetchLike } from '@pay-roadrunner/api-client-core';

let authFetchImpl: FetchLike = (input, init) => fetch(input, init);
let getBaseUrlImpl = (): string => '';

export const configureUsersAccessClient = (deps: { authFetch: FetchLike; getApiBaseUrl: () => string }): void => {
  authFetchImpl = deps.authFetch;
  getBaseUrlImpl = deps.getApiBaseUrl;
};

export type TenantUserStatus = 'active' | 'deactivated';

export interface TenantUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: TenantUserStatus;
  roles: string[];
}

export interface AssignableRole {
  name: string;
  displayName: string;
}

export interface TenantUserListPage {
  items: TenantUser[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export type TenantUserSortField = 'lastName' | 'firstName' | 'email' | 'status';

export interface TenantUserListQuery {
  search?: string;
  sortBy: TenantUserSortField;
  sortDescending: boolean;
  page: number;
  pageSize: number;
}

export interface CreateTenantUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  roles: string[];
}

export interface UpdateTenantUserProfileRequest {
  firstName: string;
  lastName: string;
  email: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  statusCode: number;
  message?: string;
  errorCode?: string;
  result?: T;
}

/** Thrown for any non-success response. Carries the server's message/errorCode so the screen can surface a safeguard rejection verbatim (e.g. "You cannot deactivate your own account."). */
export class UsersAccessApiError extends Error {
  readonly status: number;
  readonly errorCode?: string;

  constructor(message: string, status: number, errorCode?: string) {
    super(message);
    this.status = status;
    this.errorCode = errorCode;
  }
}

const base = (): string => `${getBaseUrlImpl()}/api/users`;

const parse = async <T>(response: Response): Promise<T> => {
  const text = await response.text();
  let body: ApiEnvelope<T> | undefined;
  if (text) {
    try {
      body = JSON.parse(text) as ApiEnvelope<T>;
    } catch {
      // A non-JSON body (e.g. an HTML error page from a proxy/load-balancer during a deploy)
      // must still surface as a friendly UsersAccessApiError, not a raw SyntaxError.
      throw new UsersAccessApiError(`Request failed with status ${response.status}`, response.status);
    }
  }
  if (!response.ok || !body?.success) {
    throw new UsersAccessApiError(
      body?.message ?? `Request failed with status ${response.status}`,
      response.status,
      body?.errorCode,
    );
  }
  return body.result as T;
};

const withJsonBody = (method: string, payload?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: payload === undefined ? undefined : JSON.stringify(payload),
});

export const listUsers = async (query: TenantUserListQuery, signal?: AbortSignal): Promise<TenantUserListPage> => {
  const params = new URLSearchParams({
    sortBy: query.sortBy,
    sortDescending: String(query.sortDescending),
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search) params.set('search', query.search);
  const response = await authFetchImpl(`${base()}?${params.toString()}`, { signal });
  return parse<TenantUserListPage>(response);
};

/** The canonical assignable-role source (R-010) — the UI must never maintain its own hardcoded role list. */
export const getAssignableRoles = async (): Promise<AssignableRole[]> => {
  const response = await authFetchImpl(`${base()}/roles`);
  return parse<AssignableRole[]>(response);
};

export const createUser = async (request: CreateTenantUserRequest): Promise<TenantUser> => {
  const response = await authFetchImpl(base(), withJsonBody('POST', request));
  return parse<TenantUser>(response);
};

export const updateUserProfile = async (id: string, request: UpdateTenantUserProfileRequest): Promise<TenantUser> => {
  const response = await authFetchImpl(`${base()}/${encodeURIComponent(id)}`, withJsonBody('PUT', request));
  return parse<TenantUser>(response);
};

export const updateUserRoles = async (id: string, roles: string[]): Promise<TenantUser> => {
  const response = await authFetchImpl(`${base()}/${encodeURIComponent(id)}/roles`, withJsonBody('PUT', { roles }));
  return parse<TenantUser>(response);
};

export const deactivateUser = async (id: string): Promise<TenantUser> => {
  const response = await authFetchImpl(`${base()}/${encodeURIComponent(id)}/deactivate`, withJsonBody('POST'));
  return parse<TenantUser>(response);
};

export const reactivateUser = async (id: string): Promise<TenantUser> => {
  const response = await authFetchImpl(`${base()}/${encodeURIComponent(id)}/reactivate`, withJsonBody('POST'));
  return parse<TenantUser>(response);
};

export const setUserPassword = async (id: string, password: string): Promise<void> => {
  const response = await authFetchImpl(`${base()}/${encodeURIComponent(id)}/password`, withJsonBody('POST', { password }));
  await parse<boolean>(response);
};
